import {
  RESULT_RANGE,
  resolveOpenerLocale,
  resolveOpenerTheme,
  resolveStorefrontTerminology,
} from "@loot-os/api-contract";
import { LootBoxOpener } from "@loot-os/react";
import { Badge } from "@loot-os/ui/components/badge";
import { BoxCard } from "@loot-os/ui/components/box-card";
import { BoxDropTable } from "@loot-os/ui/components/box-drop-table";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { BoxSummaryLine } from "@loot-os/ui/components/box-summary-line";
import { buttonVariants } from "@loot-os/ui/components/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { Tabs, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { ExternalLink } from "lucide-react";
import { useMemo, useState } from "react";

import type { StorefrontPreviewChrome } from "../lib/boxEditor";
import { useCurrency } from "../lib/currency";
import type { DropTableDraft } from "../lib/dropTableDraft";
import { formatNumber } from "../lib/format";
import { previewOpenerClient } from "../lib/previewOpenerClient";
import { storefrontUrl } from "../lib/storefrontUrl";
import { StorefrontThemeScope } from "./StorefrontThemeScope";

/**
 * The box as shoppers will see it, rendered from the *unsaved* draft.
 *
 * Every part is the real thing: the card, odds table and header are the same
 * `@loot-os/ui/components/box-*` the storefront renders, and the opener is the
 * actual `<LootBoxOpener>` from @loot-os/react. Nothing here reimplements the
 * shopper view, which is the only reason it can be trusted not to drift.
 *
 * The opener runs in demo mode against the draft's own odds — see
 * `previewOpenerClient` for why no write path exists.
 *
 * `storefront` is supplied by the page, not fetched here: a box template
 * belongs to no tenant, so on the global catalog editor there is no theme to
 * fetch and no store to link to. Omitting it renders the stock palette and
 * makes no request — which matters, because a tenant-scoped fetch from a page with
 * no tenant does not fail, it resolves against whichever tenant was last visited.
 * The currency comes through `useCurrency()`, which skips its fetch outside a tenant
 * for the same reason and reads amounts as dollars there.
 */
export function BoxStorefrontPreview({
  boxId,
  slug,
  name,
  imageUrl,
  version,
  isCatalog,
  openedCount,
  draft,
  storefront,
}: {
  boxId: string;
  slug: string;
  name: string;
  imageUrl: string;
  version: number;
  isCatalog: boolean;
  openedCount: number;
  draft: DropTableDraft;
  /** Omitted where the box has no one storefront — see the note above. */
  storefront?: StorefrontPreviewChrome;
}) {
  const [view, setView] = useState<"page" | "card">("page");
  const currency = useCurrency();

  // A missing config is a degraded preview, not a broken one: fall through to
  // the stock palette rather than blocking the editor on a branding fetch.
  const theme = storefront?.theme;
  const hostname = storefront?.hostname ?? null;

  // priceCents drives rarity (value ÷ price), so an unpriced draft would bucket
  // every item as Common. Say so instead of showing confidently wrong chips.
  const price = draft.price ?? 0;
  const priced = price > 0;

  // The opener rolls locally through selectFromDropTable, which throws unless
  // the weights sum to exactly RESULT_RANGE — so it only mounts on a valid
  // table. The Σ badge on the editor above is the same gate.
  const openable = draft.sumOk && priced;

  const openerTheme = useMemo(() => resolveOpenerTheme(theme), [theme]);
  const terminology = storefront?.terminology;
  const openerLocale = useMemo(
    () => resolveOpenerLocale(resolveStorefrontTerminology(terminology ?? {})),
    [terminology],
  );

  // Re-created whenever the draft changes: useLootBox refetches per client
  // identity, which is how a mounted opener picks up an edit without being
  // remounted (a remount would drop a spin in progress). draft.parsed is itself
  // memoized on the rows, so its identity changes exactly when the table does.
  const createClient = useMemo(
    () =>
      previewOpenerClient({
        id: boxId,
        name,
        slug,
        imageUrl,
        priceCents: price,
        version,
        isCatalog,
        // Merchandising, and the preview renders one box on its own rather than a home page,
        // so nothing here reads it.
        featured: false,
        openedCount,
        ev: Math.round(draft.ev),
        dropTable: draft.parsed,
        dropTableHash: "",
        refreshedAt: "",
      }),
    [boxId, name, slug, imageUrl, price, version, isCatalog, openedCount, draft.ev, draft.parsed],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Storefront preview
          <Badge variant="secondary">Unsaved draft</Badge>
        </CardTitle>
        <CardAction className="flex items-center gap-2">
          {hostname && storefront?.enabled && (
            <a
              href={storefrontUrl(hostname)}
              target="_blank"
              rel="noreferrer"
              title={hostname}
              className={buttonVariants({ variant: "ghost", size: "sm" })}
            >
              Open the real store
              <ExternalLink />
            </a>
          )}
          <Tabs value={view} onValueChange={(v) => setView(v as "page" | "card")}>
            <TabsList>
              <TabsTrigger value="page">Page</TabsTrigger>
              <TabsTrigger value="card">Card</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardAction>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        {storefront?.unavailable && (
          <p className="text-xs text-muted-foreground">
            Using the default theme — couldn't load this environment's storefront config.
          </p>
        )}
        {!storefront && (
          <p className="text-xs text-muted-foreground">
            Shown in the stock palette — every tenant's storefront themes this box itself.
          </p>
        )}
        {!priced && (
          <p className="text-xs text-muted-foreground">
            Set a price to preview rarity tiers — they're derived from item value ÷ box price.
          </p>
        )}
        {view === "page" && openable && (
          <p className="text-xs text-muted-foreground">
            Spins roll locally against this draft's odds — no opens are recorded and nothing is
            debited.
          </p>
        )}
        {!draft.sumOk && (
          <p className="text-xs text-amber-400">
            Weights sum to {formatNumber(draft.weightSum)} of {formatNumber(RESULT_RANGE)} — chances
            below are out of {formatNumber(RESULT_RANGE)}, so they won't add to 100% until you
            normalize.
          </p>
        )}

        <StorefrontThemeScope theme={theme} className="rounded-xl border p-6">
          {view === "card" ? (
            <div className="max-w-64">
              <BoxCard
                box={{ name, imageUrl, priceCents: price, openedCount, isCatalog }}
                currency={currency}
              />
            </div>
          ) : (
            <div className="grid gap-6">
              {/* Mirrors BoxDetailPage's header: the hero image lives inside
                  the opener, so repeating it here would not match the store. */}
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight">{name}</h2>
                  <BoxSummaryLine
                    priceCents={price}
                    openedCount={openedCount}
                    currency={currency}
                  />
                </div>
                <Badge variant="outline" className="font-mono text-[11px]">
                  v{version || "unpublished"}
                </Badge>
              </div>

              {/*
                The real opener, in demo mode: spins roll locally against the
                draft's own odds and nothing is written. previewOpenerClient
                explains why that is structurally guaranteed rather than a
                convention. min-h matches the shell's own 480px so the panel
                does not jump between the two states.
              */}
              {openable ? (
                <LootBoxOpener
                  key={boxId}
                  boxId={boxId}
                  createClient={createClient}
                  // Non-empty so the SDK invokes the factory at all; the factory
                  // ignores its config and returns a local stub, so this is never
                  // sent anywhere.
                  publishableKey="preview"
                  sessionToken={null}
                  theme={openerTheme}
                  locale={openerLocale}
                  sounds={false}
                  showFairness={false}
                />
              ) : (
                <div className="flex min-h-[480px] flex-col items-center justify-center gap-4 rounded-xl border border-dashed p-6 text-center">
                  <BoxImage
                    src={imageUrl}
                    alt={name}
                    className="size-40 rounded-xl object-contain"
                    fallback={<span className="text-4xl opacity-40">🎁</span>}
                  />
                  <p className="text-sm text-muted-foreground">
                    {priced
                      ? "The opener needs a valid drop table — normalize the weights above to try a spin."
                      : "The opener needs a price — set one above to try a spin."}
                  </p>
                </div>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">What's inside — full odds</CardTitle>
                </CardHeader>
                <CardContent>
                  <BoxDropTable
                    items={draft.parsed}
                    priceCents={price}
                    currency={currency}
                    showRarity={priced}
                    rarityColors={theme?.rarity}
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </StorefrontThemeScope>
      </CardContent>
    </Card>
  );
}
